import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavigationType, useNavigationType } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { SortableTh } from '../../components/ui/SortableTh';
import { IconEye } from '../../components/layout/icons';
import { ApiError } from '../../lib/api-client';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { exportRowsToExcel, fetchAllPages } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { fetchIpdDaycareAdmissions } from './ipd-daycare.api';
import { LookupSelect } from './ipd-daycare.shared';
import { useIpdDaycareAdmissions, useIpdDaycareLookup } from './ipd-daycare.queries';
import { currency, formatIpdDateTime, todayInput } from './ipd-daycare.utils';
import type { IpdDaycareAdmission, IpdDaycareListFilters } from './types';

const FILTERS_STORAGE_KEY = 'ipd-daycare-list-filters';
const exportUpperText = (value: string | number | null | undefined, fallback = '-') => {
  const text = typeof value === 'string' ? value.trim() : value == null ? '' : String(value);
  return text ? text.toUpperCase() : fallback;
};

const initialFilters = (): IpdDaycareListFilters => {
  const date = todayInput();
  return { page: 1, limit: 20, fromDate: date, toDate: date };
};

// With no status and no dates, the server's default view lists every admitted
// patient plus today's discharges; any status or date switches to a filtered list.
const isDefaultViewFilters = (filters: IpdDaycareListFilters) => !filters.status && !filters.fromDate && !filters.toDate;

const requestFilters = (filters: IpdDaycareListFilters): IpdDaycareListFilters => (
  isDefaultViewFilters(filters) ? { ...filters, view: 'default' } : filters
);

const readStoredFilters = (): IpdDaycareListFilters => {
  const fallback = initialFilters();
  try {
    const raw = window.sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<IpdDaycareListFilters> | null;
    if (!parsed || typeof parsed !== 'object') return fallback;
    return {
      page: typeof parsed.page === 'number' && parsed.page > 0 ? parsed.page : fallback.page,
      limit: typeof parsed.limit === 'number' && parsed.limit > 0 ? parsed.limit : fallback.limit,
      ...(parsed.field ? { field: parsed.field } : {}),
      ...(typeof parsed.value === 'string' ? { value: parsed.value } : {}),
      ...(parsed.status ? { status: parsed.status } : {}),
      ...(typeof parsed.fromDate === 'string' && parsed.fromDate ? { fromDate: parsed.fromDate } : {}),
      ...(typeof parsed.toDate === 'string' && parsed.toDate ? { toDate: parsed.toDate } : {}),
      ...(parsed.sortBy ? { sortBy: parsed.sortBy, sortOrder: parsed.sortOrder === 'desc' ? 'desc' as const : 'asc' as const } : {}),
    };
  } catch {
    return fallback;
  }
};

export const IpdDaycareListPage = () => {
  const returning = useNavigationType() === NavigationType.Pop;
  const [storedFilters] = useState(() => (returning ? readStoredFilters() : initialFilters()));
  const [draft, setDraft] = useState<IpdDaycareListFilters>(storedFilters);
  const [filters, setFilters] = useState<IpdDaycareListFilters>(storedFilters);
  const doctors = useIpdDaycareLookup('doctors');
  const isDefaultView = isDefaultViewFilters(filters);
  const query = useIpdDaycareAdmissions(requestFilters(filters));
  const tableSort = useTableSort(filters.sortBy ?? null, filters.sortOrder === 'desc' ? 'desc' : 'asc', (next) => {
    setFilters((current) => ({ ...current, sortBy: next.key, sortOrder: next.direction, page: 1 }));
  });
  const busy = query.isFetching;

  useEffect(() => {
    window.sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    const totalPages = query.data?.pagination.totalPages;
    if (totalPages && filters.page > totalPages) setFilters((current) => ({ ...current, page: totalPages }));
  }, [filters.page, query.data?.pagination.totalPages]);

  const apply = (event: FormEvent) => {
    event.preventDefault();
    const { fromDate, toDate, ...rest } = draft;
    const next: IpdDaycareListFilters = {
      ...rest,
      page: 1,
      ...(fromDate ? { fromDate } : {}),
      ...(toDate ? { toDate } : {}),
    };
    setDraft(next);
    setFilters(next);
  };
  const reset = () => { const next = initialFilters(); setDraft(next); setFilters(next); };
  const today = () => {
    const date = todayInput();
    const next = { ...draft, fromDate: date, toDate: date, page: 1 };
    setDraft(next);
    setFilters(next);
  };

  const exportToExcel = async () => {
    const exportFilters: IpdDaycareListFilters = { ...requestFilters(filters), page: 1, limit: 20 };
    const rows = await fetchAllPages<IpdDaycareAdmission>(
      (page, limit) => fetchIpdDaycareAdmissions({ ...exportFilters, page, limit }),
    );
    await exportRowsToExcel({
      fileName: `IPD-Daycare-Admissions-${todayInput()}`,
      title: 'IPD / Daycare Admissions',
      subtitle: isDefaultView ? "All admitted patients + today's discharges" : 'Filtered admissions list',
      columns: [
        { header: 'SN', width: 6, align: 'right', map: (_row, index) => index + 1 },
        { header: 'IPD No', width: 10, align: 'right', map: (row) => row.ipdNumber },
        { header: 'Patient Name', width: 24, map: (row) => exportUpperText(row.patientSnapshot.name) },
        { header: 'UHID', width: 16, map: (row) => exportUpperText(row.patientSnapshot.uhid) },
        { header: 'Gender', width: 10, map: (row) => exportUpperText(row.patientSnapshot.gender) },
        { header: 'Age', width: 12, map: (row) => `${row.patientSnapshot.ageYears}Y ${row.patientSnapshot.ageMonths}M ${row.patientSnapshot.ageDays}D` },
        { header: 'Phone', width: 14, map: (row) => exportUpperText(row.patientSnapshot.phone) },
        { header: 'Visit Type', width: 11, map: (row) => exportUpperText(row.visitType) },
        { header: 'Under Doctor', width: 22, map: (row) => exportUpperText(doctorNameOrDash(row.underDoctor?.name)) },
        { header: 'Department', width: 18, map: (row) => exportUpperText(row.underDoctor?.department) },
        { header: 'Admission Date', width: 20, map: (row) => formatIpdDateTime(row.admissionDate) },
        { header: 'Discharge Date', width: 20, map: (row) => row.discharge?.dischargeDate ? formatIpdDateTime(row.discharge.dischargeDate) : '-' },
        { header: 'Discharge Type', width: 16, map: (row) => exportUpperText(row.discharge?.dischargeType) },
        { header: 'Package Type', width: 20, map: (row) => exportUpperText(row.packageType) },
        { header: 'Patient Type', width: 16, map: (row) => exportUpperText(row.insuranceType) },
        { header: 'Bed Category', width: 16, map: (row) => exportUpperText(row.bedCategory.name) },
        { header: 'Bed', width: 10, map: (row) => exportUpperText(row.bed.name) },
        { header: 'Status', width: 12, map: (row) => exportUpperText(row.status) },
        { header: 'Due Amount', width: 14, align: 'right', numFmt: '"₹"#,##0.00', map: (row) => row.billing.dueAmount },
        { header: 'Credit Amount', width: 14, align: 'right', numFmt: '"₹"#,##0.00', map: (row) => row.billing.creditAmount },
      ],
      rows,
    });
  };

  return (
    <div className="page-stack ipd-daycare-page">
      <div className="page-heading"><div><p className="eyebrow">IPD / Daycare</p><h1>IPD / Daycare List</h1><p>Admissions, bed assignments, and visit details.</p></div><div className="page-heading-actions"><ExcelExportButton onExport={exportToExcel} disabled={!query.isSuccess} /><Link className="button button-primary" to="/ipd-daycare/admit">New IPD/Daycare Register</Link></div></div>
      <section className="content-card ipd-daycare-card">
        <form className="ipd-daycare-filter-grid" onSubmit={apply}>
          <select value={draft.field ?? ''} onChange={(event) => setDraft((current) => ({ ...current, field: event.target.value as IpdDaycareListFilters['field'], value: '' }))}>
            <option value="">SELECT FIELD</option><option value="patientName">PATIENT NAME</option><option value="uhid">UHID</option><option value="phone">Phone No</option><option value="ipdNo">IPD No</option><option value="underDoctor">Under Doctor</option>
          </select>
          {draft.field === 'underDoctor' ? <LookupSelect id="ipd-daycare-doctor-filter" value={draft.value ?? ''} options={doctors.data} placeholder="Select Doctor" onChange={(value) => setDraft((current) => ({ ...current, value }))} /> : <input value={draft.value ?? ''} onChange={(event) => setDraft((current) => ({ ...current, value: event.target.value }))} placeholder="Field Value" disabled={!draft.field} />}
          <select value={draft.status ?? ''} onChange={(event) => setDraft((current) => ({ ...current, status: (event.target.value || undefined) as IpdDaycareListFilters['status'] }))}>
            <option value="">All Status</option><option value="admitted">Admitted</option><option value="discharged">Discharged</option><option value="cancelled">Cancelled</option>
          </select>
          <input type="date" value={draft.fromDate ?? ''} onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))} aria-label="From date" />
          <input type="date" value={draft.toDate ?? ''} onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))} aria-label="To date" />
          <div className="ipd-daycare-filter-actions"><button className="button button-primary" type="submit" disabled={busy}>Filter</button><button className="button ipd-daycare-today" type="button" onClick={today}>Today</button><button className="button button-secondary" type="button" onClick={reset}>Reset</button></div>
        </form>
        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load IPD/Daycare admissions" message={query.error instanceof ApiError ? query.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && query.data.data.length === 0 ? <EmptyState title="No IPD/Daycare admissions found" message="Change the filters or create a new admission." /> : null}
        {query.isSuccess && query.data.data.length > 0 ? <>
          <div className="table-scroll"><table className="ipd-daycare-table"><thead><tr><th>SN</th><SortableTh control={tableSort} column="ipdNo">IPD No</SortableTh><SortableTh control={tableSort} column="patient">Patient Details</SortableTh><SortableTh control={tableSort} column="doctor">Under Doctor</SortableTh><SortableTh control={tableSort} column="admissionDate">Details</SortableTh><SortableTh control={tableSort} column="bed">Bed Details</SortableTh><SortableTh control={tableSort} column="dueCredit">Due / Credit</SortableTh><th>Action</th></tr></thead><tbody>{query.data.data.map((row, index) => <tr key={row.id} className={[row.status, row.billing.dueAmount > 0 ? 'row-due' : ''].filter(Boolean).join(' ')}>
            <td>{(filters.page - 1) * filters.limit + index + 1}</td><td><strong>{row.ipdNumber}</strong><span className={`ipd-daycare-badge ${row.status}`}>{exportUpperText(row.status)}</span></td>
            <td>
              <span className="ipd-daycare-name-line">
                <Link className="patient-name-link" to={`/patients/${row.patient.id}`} title="Open patient profile"><strong>{exportUpperText(row.patientSnapshot.name)}</strong></Link>
                <span className={`ipd-daycare-badge ${row.visitType}`}>{exportUpperText(row.visitType)}</span>
              </span>
              {row.patientSnapshot.uhid ? <small>{exportUpperText(row.patientSnapshot.uhid)}</small> : null}
              <small>{exportUpperText(row.patientSnapshot.gender)} · {row.patientSnapshot.ageYears}Y {row.patientSnapshot.ageMonths}M {row.patientSnapshot.ageDays}D</small>
              {row.patientSnapshot.phone ? <small>{exportUpperText(row.patientSnapshot.phone)}</small> : null}
            </td>
            <td>
              <small>Doct: {exportUpperText(doctorNameOrDash(row.underDoctor?.name))}</small>
              {row.otherUnderDoctor?.name ? <small>Other Doct: {exportUpperText(doctorNameOrDash(row.otherUnderDoctor?.name))}</small> : null}
            </td>
            <td>
              <small>Adm Dt: {formatIpdDateTime(row.admissionDate)}</small>
              {row.status === 'discharged' ? (
                <>
                  <small>Dis Dt: {row.discharge?.dischargeDate ? formatIpdDateTime(row.discharge.dischargeDate) : '-'}</small>
                  <small>Discharge Type: {exportUpperText(row.discharge?.dischargeType)}</small>
                </>
              ) : null}
              <small>Dept: {exportUpperText(row.underDoctor?.department)}</small>
              <small>Package Type: {exportUpperText(row.packageType)}</small>
              <small>Patient Type: {exportUpperText(row.insuranceType)}</small>
            </td>
            <td>
              <strong>{exportUpperText(row.bed.name)}</strong>
              <small>{exportUpperText(row.bedCategory.name)}</small>
            </td>
            <td>{row.billing.dueAmount > 0
              ? <span className="ipd-daycare-due">Due: {currency(row.billing.dueAmount)}</span>
              : row.billing.creditAmount > 0
                ? <span className="ipd-daycare-credit">Credit: {currency(row.billing.creditAmount)}</span>
                : <span className="ipd-daycare-settled">{currency(0)}</span>}</td>
            <td><Link className="ipd-daycare-row-action" to={row.billing.hasBill ? `/ipd-daycare/${row.id}?tab=bill-details` : `/ipd-daycare/${row.id}`} title="View" aria-label="View admission"><IconEye /></Link></td>
          </tr>)}</tbody></table></div>
          <div className="pagination ipd-daycare-pagination"><label>Rows <select value={filters.limit} disabled={busy} onChange={(event) => { const limit = Number(event.target.value); setDraft((current) => ({ ...current, limit })); setFilters((current) => ({ ...current, limit, page: 1 })); }}><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option><option value={500}>500</option></select></label><button className="button button-secondary" disabled={busy || filters.page <= 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}>Previous</button><span>Page {filters.page} of {Math.max(query.data.pagination.totalPages, 1)}</span><button className="button button-secondary" disabled={busy || filters.page >= query.data.pagination.totalPages} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>Next</button></div>
        </> : null}
      </section>
    </div>
  );
};
